/* eslint-disable max-len */
'use strict';

/* ── config ──────────────────────────────────────────────── */

const TYPING_SECTION_SELECTOR = '.e-stn--title-description-and-typing-animation-card';
const TYPING_WRAPPER_SELECTOR = '.e-div--content-wrapper';
const TYPING_SOURCE_CLASS = 'js-typing-source';
const TYPING_OUTPUT_CLASS = 'js-typing-output';
const TYPING_READY_CLASS = 'is-typing-ready';
const TYPING_VIEWPORT_RATIO = 0.25;

// Speeds (ms) — increase TYPE_SPEED to slow down typing, decrease to speed up
const TYPE_SPEED = 3.5; // ms per character typed   (higher = slower)
const DELETE_SPEED = 2; // ms per character deleted (higher = slower)
const HOLD_AFTER_TYPE_MS = 1600; // pause after fully typed
const HOLD_AFTER_DEL_MS = 280; // pause before typing next

/* ── helpers ─────────────────────────────────────────────── */

function typingCleanText( el ) {
  return ( el.textContent || '' ).replace( /\s+/g, ' ' ).trim();
}

/* ── DOM builder ─────────────────────────────────────────── */

function typingBuildOutput( wrapper ) {
  const sources = Array.from( wrapper.querySelectorAll( ':scope > p' ) );
  const texts = sources.map( typingCleanText ).filter( Boolean );
  if ( !texts.length ) return null;

  sources.forEach( ( p ) => {
    p.classList.add( TYPING_SOURCE_CLASS );
    p.setAttribute( 'aria-hidden', 'true' );
  } );

  const output = sources[ 0 ].cloneNode( false );
  output.classList.remove( TYPING_SOURCE_CLASS );
  output.classList.add( TYPING_OUTPUT_CLASS );
  output.removeAttribute( 'aria-hidden' );
  output.setAttribute( 'aria-live', 'polite' );
  output.textContent = '';
  wrapper.appendChild( output );
  wrapper.classList.add( TYPING_READY_CLASS );

  return { texts, output };
}

/* ── tick helpers ────────────────────────────────────────── */

function typingScheduleNext( state, delay ) {
  state.timerId = setTimeout( () => state.tick(), delay );
}

function typingTickForward( state, current ) {
  state.charIdx += 1;
  state.output.textContent = current.slice( 0, state.charIdx );
  typingScheduleNext( state, TYPE_SPEED );
}

function typingTickFullyTyped( state ) {
  if ( state.texts.length === 1 ) { state.running = false; return; }
  state.deleting = true;
  typingScheduleNext( state, HOLD_AFTER_TYPE_MS );
}

function typingTickBackward( state, current ) {
  state.charIdx -= 1;
  state.output.textContent = current.slice( 0, state.charIdx );
  typingScheduleNext( state, DELETE_SPEED );
}

function typingTickFullyDeleted( state ) {
  state.deleting = false;
  state.textIdx = ( state.textIdx + 1 ) % state.texts.length;
  typingScheduleNext( state, HOLD_AFTER_DEL_MS );
}

function typingTick( state ) {
  if ( !state.running ) return;
  const current = state.texts[ state.textIdx ];
  if ( !state.deleting && state.charIdx < current.length ) { typingTickForward( state, current ); return; }
  if ( !state.deleting ) { typingTickFullyTyped( state ); return; }
  if ( state.charIdx > 0 ) { typingTickBackward( state, current ); return; }
  typingTickFullyDeleted( state );
}

/* ── controller ──────────────────────────────────────────── */

function typingStop( state ) {
  if ( state.timerId !== null ) { clearTimeout( state.timerId ); state.timerId = null; }
  state.running = false;
}

function typingStart( state ) {
  typingStop( state );
  state.textIdx = 0;
  state.charIdx = 0;
  state.deleting = false;
  state.output.textContent = '';
  state.running = true;
  state.tick();
}

function typingMakeController( dom ) {
  const state = { texts: dom.texts, output: dom.output, textIdx: 0, charIdx: 0, deleting: false, timerId: null, running: false, tick: null };
  state.tick = () => typingTick( state );
  return {
    start: () => typingStart( state ),
    stop:  () => typingStop( state ),
  };
}

/* ── intersection observer ───────────────────────────────── */

function typingObserveEntry( entry, ctrl ) {
  if ( entry.isIntersecting ) { ctrl.start(); } else { ctrl.stop(); }
}

function typingObserve( wrapper, ctrl ) {
  const io = new IntersectionObserver(
    ( entries ) => entries.forEach( ( entry ) => typingObserveEntry( entry, ctrl ) ),
    { root: null, rootMargin: '0px', threshold: TYPING_VIEWPORT_RATIO }
  );
  io.observe( wrapper );
}

/* ── init ────────────────────────────────────────────────── */

function typingInitWrapper( wrapper ) {
  const dom = typingBuildOutput( wrapper );
  if ( !dom ) return;
  typingObserve( wrapper, typingMakeController( dom ) );
}

function typingInitSection( section ) {
  section.querySelectorAll( TYPING_WRAPPER_SELECTOR ).forEach( typingInitWrapper );
}

function typingInit() {
  if ( document.querySelector( '.editor-styles-wrapper' ) ) return;
  document.querySelectorAll( TYPING_SECTION_SELECTOR ).forEach( typingInitSection );
}

/* ── bootstrap ───────────────────────────────────────────── */

if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', typingInit );
} else {
  typingInit();
}
